import { alpha } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import colors from './colors';

// A custom theme for this app
const theme = createTheme({
  cssVariables: true,
  palette: {
    primary: {
      main: colors.darkPurple,
      light: colors.lightPurple,
    },
    secondary: {
      main: colors.darkGreen,
      light: colors.lightGreen,
    },
    divider: colors.lightGreen,
    text: {
      disabled: colors.grey,
    },
    background: {
      main: alpha(colors.black, 0.07),
    },
  },
  typography: {
    fontSize: 13.125, // 15px
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontSize: 24,
      fontWeight: 700,
      color: colors.darkPurple,
    },
    h2: {
      fontSize: 15,
      fontWeight: 700,
      color: colors.darkGreen,
      textTransform: 'uppercase',
    },
    h3: {
      fontSize: 15,
      fontWeight: 600,
      color: colors.grey,
    },
    h4: {
      fontSize: 18,
      fontWeight: 500,
      color: colors.grey,
    },
    h5: {
      fontSize: 16,
      fontWeight: 500,
      color: colors.grey,
    },
    h6: {
      fontSize: 14,
      fontWeight: 500,
      color: colors.grey,
    },
    subtitle1: {
      fontSize: 11,
      color: colors.grey,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 700,
          fontSize: 14,
          '&:focus': {
            outlineColor: colors.lightGrey,
          },
          variants: [
            {
              props: { variant: 'outlined' },
              style: {
                padding: '3px 32px',
                borderWidth: 2,
                textTransform: 'none',
                backgroundColor: colors.white,
                minHeight: 'auto',
              },
            },
            {
              props: { variant: 'contained' },
              style: {
                padding: '3px 32px',
              },
            },
            {
              props: { variant: 'link' },
              style: {
                fontWeight: 400,
                textTransform: 'none',
                textDecoration: 'underline',
                paddingX: 2,
                paddingY: '3px',
                color: colors.darkGreen,
              },
            },
          ],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus': {
            outlineColor: colors.lightGrey,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: 12,
          variants: [
            {
              props: { variant: 'h1' },
              style: {
                marginBottom: 24,
              },
            },
          ],
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.black,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MenuList-root': {
            border: '1px solid',
            borderColor: colors.lightGreen,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: 6,
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.lightGreen,
              },
            },
            '&.Mui-disabled, &.Mui-readOnly': {
              backgroundColor: colors.lightGrey,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&:-webkit-autofill': {
            WebkitBoxShadow: 'initial',
            WebkitTextFillColor: 'initial',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: '2px solid',
          borderColor: colors.lightGreen,
          borderRadius: 6,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: alpha(colors.lightGreen, 0.15),
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: colors.darkGreen,
          fontWeight: 700,
          minHeight: 40,
          paddingBottom: 1,
          backgroundColor: colors.white,
          borderRadius: '20px 20px 0 0',
          marginRight: 1,
          '&.Mui-selected': {
            border: '2px solid',
            borderColor: colors.lightGreen,
            color: colors.darkGreen,
            borderBottom: 'none',
          },
          '&:focus': {
            outline: 'none',
          },
        },
        labelIcon: {
          '& span.MuiTypography-root': {
            fontWeight: 700,
            fontSize: 13.125,
            position: 'relative',
            zIndex: 2,
          },
          '& span.MuiTypography-root:nth-of-type(2)': {
            color: colors.black,
            textTransform: 'none',
            fontWeight: 400,
            fontSize: 12,
          },
        },
        icon: {
          color: colors.lightGrey,
          position: 'absolute',
          left: -10,
          top: 4,
          width: 50,
          height: 50,
          zIndex: 1,
        },
        label: {
          position: 'relative',
          zIndex: 2,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          border: 'none',
          variants: [
            {
              props: { variant: 'rounded' },
              style: {
                borderRadius: 20,
              },
            },
          ],
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-root': {
            backgroundColor: colors.white,
          },
          '&.MuiTableRow-root:nth-of-type(2n)': {
            backgroundColor: colors.smoke,
          },
          '&.MuiTableRow-hover:hover': {
            cursor: 'pointer',
            backgroundColor: alpha(colors.darkPurple, 0.1),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        body: {
          borderColor: colors.lightGrey,
          paddingTop: 10,
          paddingBottom: 10,
        },
        head: {
          backgroundColor: colors.lightPurple,
          color: colors.darkPurple,
          borderColor: colors.darkPurple,
          fontWeight: 700,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          cursor: 'pointer',
          color: colors.darkGreen,
          textDecorationColor: 'initial',
          '&:hover': {
            color: colors.darkGreen,
            textDecoration: 'none',
          },
          variants: [
            {
              props: { variant: 'grey' },
              style: {
                color: colors.grey,
                fontWeight: 400,
              },
            },
          ],
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: colors.black,
        },
        asterisk: {
          color: colors.lightGreen,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: colors.black,
        },
        asterisk: {
          color: colors.lightGreen,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: '2px solid',
          borderColor: colors.lightGreen,
          borderRadius: 10,
        },
      },
    },
    MuiChartsGrid: {
      styleOverrides: {
        line: {
          stroke: colors.lightGrey,
        },
      },
    },
  },
});

export default theme;
